/** Browser-only preferences; no names, contact details, timestamps or API calls. */
export type GuideRole = 'admin' | 'employee';
export type GuideIntroStatus = 'pending' | 'deferred' | 'completed' | 'dismissed';

export interface GuideState {
  introStatus: GuideIntroStatus;
  seenUpdateIds: string[];
  completedGuideIds: string[];
}

const VERSION = 1;
const MAX_IDS = 100;
const MAX_PAYLOAD_LENGTH = 32 * 1024;
const ID_PATTERN = /^[a-z0-9][a-z0-9._-]{0,95}$/;
const INTRO_STATUSES: GuideIntroStatus[] = ['pending', 'deferred', 'completed', 'dismissed'];
const remembered = new Map<string, GuideState>();
const memoryOnly = new Set<string>();

const emptyState = (): GuideState => ({ introStatus: 'pending', seenUpdateIds: [], completedGuideIds: [] });
const copy = (state: GuideState): GuideState => ({
  introStatus: state.introStatus,
  seenUpdateIds: [...state.seenUpdateIds],
  completedGuideIds: [...state.completedGuideIds],
});
const validId = (value: unknown): value is string => typeof value === 'string' && ID_PATTERN.test(value);
const validStatus = (value: unknown): value is GuideIntroStatus =>
  typeof value === 'string' && INTRO_STATUSES.includes(value as GuideIntroStatus);

function storageKey(userId: number, role: GuideRole): string | null {
  if (!Number.isSafeInteger(userId) || userId <= 0 || (role !== 'admin' && role !== 'employee')) return null;
  return `febest.platform-guide.v${VERSION}:${userId}:${role}`;
}

function browserStorage(): Storage | null {
  return typeof window === 'undefined' ? null : window.localStorage || null;
}

function parseIds(value: unknown): string[] | null {
  if (!Array.isArray(value) || value.length > MAX_IDS || !value.every(validId)) return null;
  return Array.from(new Set(value));
}

function parseState(raw: string | null): GuideState {
  if (!raw || raw.length > MAX_PAYLOAD_LENGTH) return emptyState();
  try {
    const value: unknown = JSON.parse(raw);
    if (!value || typeof value !== 'object' || Array.isArray(value)) return emptyState();
    const record = value as Record<string, unknown>;
    if (record.version !== VERSION || !validStatus(record.introStatus)) return emptyState();
    const seenUpdateIds = parseIds(record.seenUpdateIds);
    const completedGuideIds = parseIds(record.completedGuideIds);
    if (!seenUpdateIds || !completedGuideIds) return emptyState();
    // Only these fields are copied. Unknown fields never reach state or storage.
    return { introStatus: record.introStatus, seenUpdateIds, completedGuideIds };
  } catch {
    return emptyState();
  }
}

/** Every call returns its own arrays, including when using the memory fallback. */
export function getGuideState(userId: number, role: GuideRole): GuideState {
  const key = storageKey(userId, role);
  if (!key) return emptyState();
  if (memoryOnly.has(key)) return copy(remembered.get(key) || emptyState());
  try {
    const storage = browserStorage();
    if (!storage) return copy(remembered.get(key) || emptyState());
    const state = parseState(storage.getItem(key));
    remembered.set(key, copy(state));
    return copy(state);
  } catch {
    return copy(remembered.get(key) || emptyState());
  }
}

function save(userId: number, role: GuideRole, state: GuideState): GuideState {
  const key = storageKey(userId, role);
  if (!key) return emptyState();
  remembered.set(key, copy(state));
  try {
    const storage = browserStorage();
    if (!storage) throw new Error('Browser storage unavailable');
    storage.setItem(key, JSON.stringify({ version: VERSION, ...copy(state) }));
    memoryOnly.delete(key);
  } catch {
    // Keep explicit choices for this page even when storage is blocked/full.
    memoryOnly.add(key);
  }
  return copy(state);
}

export function setGuideIntroStatus(userId: number, role: GuideRole, status: GuideIntroStatus): GuideState {
  const state = getGuideState(userId, role);
  if (!validStatus(status) || status === state.introStatus) return state;
  return save(userId, role, { ...state, introStatus: status });
}

function appendId(ids: string[], id: string): string[] {
  return [...ids, id].slice(-MAX_IDS);
}

/** Mark only this release as seen; deferring an introduction must not call this. */
export function markGuideSeen(userId: number, role: GuideRole, updateId: string): GuideState {
  const state = getGuideState(userId, role);
  if (!validId(updateId) || state.seenUpdateIds.includes(updateId)) return state;
  return save(userId, role, { ...state, seenUpdateIds: appendId(state.seenUpdateIds, updateId) });
}

/** A scenario checkmark is independent of the introduction and update notices. */
export function markGuideCompleted(userId: number, role: GuideRole, guideId: string): GuideState {
  const state = getGuideState(userId, role);
  if (!validId(guideId) || state.completedGuideIds.includes(guideId)) return state;
  return save(userId, role, { ...state, completedGuideIds: appendId(state.completedGuideIds, guideId) });
}
